import React from 'react';
import { Printer, X } from 'lucide-react';
import QRCodeGenerator from '../Utils/QRCodeGenerator';
import { dateTimeFormatter } from '../Utils/DateTimeFormatter';
import { useAuth } from '../../UserAccounts/AuthProvider';
import './PrintAppointment.css';

/**
 * Reusable Official Print Appointment Component Modal
 * Renders Hospital Logo/Header, Patient Details, Appointment Details, Token, and QR Code.
 */
const PrintAppointment = ({ isOpen, onClose, patient }) => {
  const { userData } = useAuth();
  const printedByName = userData?.username || userData?.name || userData?.userName || userData?.fullName || 'System';

  if (!isOpen || !patient) return null;

  const handlePrint = () => {
    window.print();
  };

  const renderSlipContent = () => (
    <div className="appointment-slip-container">
      {/* Hospital Letterhead Header */}
      <div className="slip-header-brand">
        <img src="/VThospitalsLogo.png" alt="Hospital Logo" className="slip-hospital-logo" />
        <h2 className="slip-clinic-name">VISWATEJA HOSPITAL & DIAGNOSTIC CENTRE</h2>
        <p className="slip-clinic-address">26-4-788/1, RPGT Road, Near Srinivasa Talkies, Hindupur - 515201</p>
      </div>

      {/* Title Bar with Token Badge */}
      <div className="slip-title-bar">
        <span>OUTPATIENT APPOINTMENT SLIP</span>
        <span className="slip-token-tag">TOKEN #{patient.tokenNumber || '1'}</span>
      </div>

      {/* Patient & Appointment Information Table */}
      <table className="slip-info-table">
        <tbody>
          <tr>
            <td className="lbl-td">Patient Name:</td>
            <td className="val-td strong-val">{patient.patientName || 'N/A'}</td>
            <td className="lbl-td">MR Number:</td>
            <td className="val-td font-mono blue-val">{patient.mrNumber || 'N/A'}</td>
          </tr>
          <tr>
            <td className="lbl-td">Age / Sex:</td>
            <td className="val-td">{patient.age || 'N/A'} Y / {patient.gender || 'N/A'}</td>
            <td className="lbl-td">Phone:</td>
            <td className="val-td">
              {patient.phoneNumber || patient.phone || patient.contactNumber || patient.mobileNumber || 'N/A'}
            </td>
          </tr>
          <tr>
            <td className="lbl-td">Doctor:</td>
            <td className="val-td strong-val">{patient.doctorName || 'N/A'}</td>
            <td className="lbl-td">Appt No:</td>
            <td className="val-td font-mono">{patient.appointmentId || 'N/A'}</td>
          </tr>
          <tr>
            <td className="lbl-td">Booking Type:</td>
            <td className="val-td">{patient.bookingType || 'N/A'}</td>
            <td className="lbl-td">Appt Date/Time:</td>
            <td className="val-td green-val">{dateTimeFormatter(patient.appointmentDate, patient.timeSlot)}</td>
          </tr>
          <tr>
            <td className="lbl-td">Status:</td>
            <td className="val-td">{patient.appointmentStatus || 'SCHEDULED'}</td>
            <td className="lbl-td">Address:</td>
            <td className="val-td">{patient.address || 'N/A'}</td>
          </tr>
        </tbody>
      </table>

      {/* Bottom Section: QR Code & Metadata */}
      <div className="slip-bottom-row">
        <div className="slip-print-meta">
          <span><strong>Printed On:</strong> {new Date().toLocaleString('en-IN')}</span>
          <span><strong>Printed By:</strong> {printedByName}</span>
          <span>VT Hospitals</span>
        </div>
        <div className="slip-qr-box">
          <QRCodeGenerator value={patient.mrNumber} size={85} caption={patient.mrNumber} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* On-Screen Modal Preview */}
      <div className="modern-modal-overlay" onClick={onClose}>
        <div className="modern-modal-card animated-modal appt-slip-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="modal-top-accent-bar" />

          {/* Modal Header */}
          <div className="modern-modal-header">
            <div className="modal-title-container">
              <div className="modal-icon-badge blue-glow">
                <Printer size={20} className="icon-blue" />
              </div>
              <div>
                <h3 className="modal-main-heading">Print Appointment Slip</h3>
                <p className="modal-sub-heading">Official Outpatient consultation slip with hospital letterhead and QR</p>
              </div>
            </div>
            <button className="modal-close-icon-btn" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>

          {/* Printable Slip Preview */}
          <div className="modern-modal-body appt-slip-preview-body">
            {renderSlipContent()}
          </div>

          {/* Modal Footer */}
          <div className="modern-modal-footer">
            <button className="modern-btn-secondary" onClick={onClose}>
              Close
            </button>
            <button className="modern-btn-primary" onClick={handlePrint}>
              <Printer size={16} />
              <span>Print Appointment Slip</span>
            </button>
          </div>
        </div>
      </div>

      {/* Dedicated Root-Level Print Area for window.print() */}
      <div className="print-area appt-slip-print-root">
        {renderSlipContent()}
      </div>
    </>
  );
};

export default PrintAppointment;
